/*
 * @Author: dinggengren
 * @Date: 2026-04-17
 * @Company: www.smm.cn
 * @Description: 密码规则浮窗 - 包裹任意密码输入框，聚焦时展示规则实时校验状态
 */
"use client"

import type { TooltipPlacement } from "antd/es/tooltip"
import { useTranslations } from "next-intl"
import React, { cloneElement, useEffect, useMemo, useState } from "react"
import { Popover } from "antd"

import { isMobile } from "../../utils"
import { defaultPasswordRules, type PasswordRule } from "./rules"

import "./index.scss"

interface PasswordRulesPopoverProps {
  /** 由外层 Form.Item 注入 */
  value?: string
  /** 由外层 Form.Item 注入 */
  onChange?: (e: unknown) => void
  /** 自定义规则，默认使用 defaultPasswordRules() */
  rules?: PasswordRule[]
  /** 浮窗位置 */
  placement?: TooltipPlacement
  /** 浮窗挂载容器（modal / drawer 场景常用） */
  getPopupContainer?: (node: HTMLElement) => HTMLElement
  /** 必须是受控的输入框元素（antd Input.Password / 自定义 Input 等） */
  children: React.ReactElement<Record<string, unknown>>
}

/**
 * 密码规则浮窗
 *
 * 用法：
 * ```tsx
 * const t = useTranslations()
 * <Form.Item name="password" rules={[{ validator: createPasswordValidator(t) }]}>
 *   <PasswordRulesPopover>
 *     <Input.Password />
 *   </PasswordRulesPopover>
 * </Form.Item>
 * ```
 */
export const PasswordRulesPopover: React.FC<PasswordRulesPopoverProps> = ({
  value = "",
  onChange,
  rules,
  placement = "right",
  getPopupContainer,
  children,
}) => {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const [isH5, setIsH5] = useState(false)
  const resolvedRules = useMemo(() => rules ?? defaultPasswordRules(t), [rules, t])

  useEffect(() => {
    setIsH5(isMobile())
  }, [])

  const childProps = children.props as {
    onChange?: (e: unknown) => void
    onFocus?: (e: unknown) => void
    onBlur?: (e: unknown) => void
  }

  const merged = cloneElement(children, {
    value,
    onChange: (e: unknown) => {
      childProps.onChange?.(e)
      onChange?.(e)
    },
    onFocus: (e: unknown) => {
      childProps.onFocus?.(e)
      if (!isH5) setOpen(true)
    },
    onBlur: (e: unknown) => {
      childProps.onBlur?.(e)
      setOpen(false)
    },
  })

  if (isH5) {
    return merged
  }

  return (
    <Popover
      open={open}
      placement={placement}
      getPopupContainer={getPopupContainer}
      rootClassName="password-rules-popover"
      content={
        <ul className="password-rules-list">
          {resolvedRules.map((r) => {
            const pass = !!value && r.check(value)
            return (
              <li key={r.key} className={pass ? "pass" : "fail"}>
                <span className="icon" aria-hidden>
                  {pass ? "✓" : "✕"}
                </span>
                <span className="text">{r.label}</span>
              </li>
            )
          })}
        </ul>
      }
    >
      {merged}
    </Popover>
  )
}

export default PasswordRulesPopover
export { createPasswordValidator, defaultPasswordRules, isPasswordValid } from "./rules"
export type { PasswordRule } from "./rules"
